import type { ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle, CastMember, CrewMember } from "../types";
import { CastCrewRow } from "./CastCrewRow";

interface CastCrewTableProps {
  readonly titles: BulkEditTitle[];
  readonly selectedIds: Set<string>;
  readonly onSelectAll: (selected: boolean) => void;
  readonly onSelectOne: (titleId: string, selected: boolean) => void;
  readonly onFieldUpdate: (
    titleId: string,
    field: string,
    value: string | number | boolean | string[] | CastMember[] | CrewMember[] | null,
  ) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
}

/**
 * Cast & Crew view table
 * Shows: Title, Director, Cast, Crew, Status
 */
export function CastCrewTable({
  titles,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onFieldUpdate,
  onDelete,
  onDuplicate,
}: Readonly<CastCrewTableProps>): ReactElement {
  const allSelected = titles.length > 0 && titles.every((t) => selectedIds.has(t.id));

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-neutral-900/50">
            <TableHead className="w-12">
              <Checkbox checked={allSelected} onCheckedChange={onSelectAll} aria-label="Select all" />
            </TableHead>
            <TableHead className="w-[250px] flex pt-[14px]">Title</TableHead>
            <TableHead className="w-[230px]">Director</TableHead>
            <TableHead className="w-[280px]">Cast</TableHead>
            <TableHead className="w-[280px]">Crew</TableHead>
            <TableHead className="w-[170px]">Status</TableHead>
            <TableHead className="w-[100px] text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {titles.map((title) => (
            <CastCrewRow
              key={title.id}
              title={title}
              isSelected={selectedIds.has(title.id)}
              onSelect={onSelectOne}
              onFieldUpdate={onFieldUpdate}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
